import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Layers, Download, Eye, Search, RefreshCw, ExternalLink } from 'lucide-react';
import AttachmentPreview from '@/components/AttachmentPreview';

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
async function downloadFile(name, url) {
  if (!url) { toast.error('No file'); return; }
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = name || 'drawing.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objUrl);
  } catch { toast.error('Download failed'); }
}

const STAGE_ORDER = ['Design', 'Execution', 'Issued', 'Handover', 'CAD Drawings'];
const stageColor = (s) => ({
  Design: 'bg-[#3A8C8C]/12 text-[#3A8C8C]',
  Execution: 'bg-[#C67D5B]/12 text-[#C67D5B]',
  Issued: 'bg-[#7DA68A]/15 text-[#7DA68A]',
  Handover: 'bg-[#7DA68A]/15 text-[#7DA68A]',
  'CAD Drawings': 'bg-[#C4727F]/12 text-[#C4727F]',
}[s] || 'bg-[#E8E6E1] text-[#6B7280]');

export default function DrawingsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [q, setQ] = useState('');
  const [viewFile, setViewFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/drawings/all');
      setRows(res.data || []);
    } catch { toast.error('Failed to load drawings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const projects = useMemo(() => {
    const m = new Map();
    rows.forEach(r => m.set(r.project_id, r.project_name));
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const stages = useMemo(() => {
    const set = new Set(rows.map(r => r.stage));
    return [...set].sort((a, b) => (STAGE_ORDER.indexOf(a) + 1 || 99) - (STAGE_ORDER.indexOf(b) + 1 || 99));
  }, [rows]);

  const filtered = rows.filter(r => {
    if (filterProject !== 'all' && r.project_id !== filterProject) return false;
    if (filterStage !== 'all' && r.stage !== filterStage) return false;
    if (q && !`${r.name} ${r.project_name} ${r.stage}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const byStage = useMemo(() => {
    const g = {};
    for (const r of filtered) (g[r.stage] = g[r.stage] || []).push(r);
    return Object.entries(g).sort((a, b) => (STAGE_ORDER.indexOf(a[0]) + 1 || 99) - (STAGE_ORDER.indexOf(b[0]) + 1 || 99));
  }, [filtered]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="drawings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="section-heading-row text-2xl font-['Manrope'] font-bold flex items-center gap-2"><Layers size={22} /> Drawings Register</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">All drawings across every project, grouped by stage — updates live as files move between folders.</p>
        </div>
        <button onClick={load} className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#D4D0C8] text-xs font-medium text-[#6B7280] hover:bg-[#E8E6E1]" data-testid="drawings-refresh"><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search drawings…" data-testid="drawings-search"
            className="h-9 w-56 pl-8 pr-3 rounded-lg border border-[#D4D0C8] text-sm bg-white outline-none focus:border-[#3A8C8C]" />
        </div>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="h-9 w-48 rounded-lg border-[#D4D0C8] text-xs" data-testid="drawings-filter-project"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="h-9 w-40 rounded-lg border-[#D4D0C8] text-xs" data-testid="drawings-filter-stage"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">{filtered.length} drawing{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-2xl text-center py-16">
          <Layers size={36} className="text-[#D4D0C8] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#6B7280]">No drawings match your filters.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {byStage.map(([stage, items]) => (
            <div key={stage} className="glass-card rounded-2xl overflow-hidden" data-testid={`stage-group-${stage}`}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8E6E1]">
                <Badge className={`text-[10px] ${stageColor(stage)}`}>{stage}</Badge>
                <span className="text-xs text-[#6B7280]">{items.length} drawing{items.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAF8]">
                      <TableHead className="text-xs">Drawing</TableHead>
                      <TableHead className="text-xs">Project</TableHead>
                      <TableHead className="text-xs">Folder Path</TableHead>
                      <TableHead className="text-xs">Version</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Size</TableHead>
                      <TableHead className="text-xs">Updated</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(r => (
                      <TableRow key={`${r.project_id}-${r.drawing_id}`} className="hover:bg-[#FAFAF8]/60" data-testid={`sheet-row-${r.drawing_id}`}>
                        <TableCell className="text-sm font-medium text-[#2C2C2E]">{r.name}</TableCell>
                        <TableCell><button onClick={() => navigate(`/projects/${r.project_id}`)} className="text-xs text-[#3A8C8C] hover:underline flex items-center gap-1">{r.project_name} <ExternalLink size={10} /></button></TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{r.folder_path}</TableCell>
                        <TableCell><Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[9px]">V{r.version}{r.version_count > 1 ? ` · ${r.version_count}` : ''}</Badge></TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{r.status || '-'}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{formatSize(r.size)}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{formatDate(r.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <button onClick={() => setViewFile({ name: r.name, data_url: r.data_url, type: r.type, size: r.size })} className="p-1.5 rounded hover:bg-[#3A8C8C]/10 text-[#3A8C8C]" title="View"><Eye size={14} /></button>
                            <button onClick={() => downloadFile(r.name, r.data_url)} className="p-1.5 rounded hover:bg-[#3A8C8C]/10 text-[#3A8C8C]" title="Download"><Download size={14} /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      <AttachmentPreview attachment={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
}
